<template>
  <div class="import-page">
    <div style="margin-bottom: 10px">
      <el-button-group>
        <el-button icon="el-icon-download" :loading="tplLoading.sales" @click="downloadTemplate('sales', '销售数据导入模板.xls')">销售数据模板</el-button>
        <el-button icon="el-icon-download" :loading="tplLoading.optometry" @click="downloadTemplate('optometry', '验光数据导入模板.xls')">验光数据模板</el-button>
      </el-button-group>
    </div>
    <el-tabs v-model="activeTab" type="card">
      <!-- ================= Tab1 销售数据导入 ================= -->
      <el-tab-pane label="销售数据导入" name="sales">
        <el-card shadow="never">
          <!-- 文件上传 -->
          <el-upload
            drag
            action="#"
            :auto-upload="false"
            :on-change="onFileChange"
            :on-remove="onFileRemove"
            :limit="1"
            :file-list="sales.fileList"
            accept=".xls,.xlsx,.html"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击选择</em></div>
            <div slot="tip" class="el-upload__tip">支持 .xls / .xlsx / .html 文件，单个文件不超过 20MB；选择文件后自动解析预览</div>
          </el-upload>

          <!-- 预览统计卡片 -->
          <el-row :gutter="16" v-if="sales.previewData">
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ sales.previewData.totalRows }}</div><div class="stat-label">总行数</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ sales.previewData.validRows }}</div><div class="stat-label">有效行数</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ sales.previewData.orderCount }}</div><div class="stat-label">订单数</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ sales.previewData.customerToCreate }}</div><div class="stat-label">需新建客户</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ sales.previewData.skipped }}</div><div class="stat-label">跳过数</div></el-card></el-col>
          </el-row>

          <!-- 确认导入按钮 -->
          <div class="action-bar" v-if="sales.file">
            <el-button
              type="primary"
              icon="el-icon-check"
              :loading="sales.committing"
              :disabled="!sales.previewData"
              @click="commitImport('sales')"
            >确认导入</el-button>
            <el-button
              v-if="sales.commitData && sales.commitData.errors && sales.commitData.errors.length"
              type="warning"
              icon="el-icon-download"
              @click="downloadErrorCsv('销售', sales.commitData.errors)"
            >下载错误CSV</el-button>
          </div>

          <!-- 预览明细表（后端返回 preview 时才展示） -->
          <template v-if="getPreviewRows(sales.previewData).length">
            <h4 class="section-title">预览明细（最多前 20 条）</h4>
            <el-table :data="getPreviewRows(sales.previewData).slice(0, 20)" border size="small" stripe>
              <el-table-column
                v-for="col in getPreviewColumns(sales.previewData)"
                :key="col"
                :prop="col"
                :label="col"
                show-overflow-tooltip
              ></el-table-column>
            </el-table>
          </template>

          <!-- 错误清单 -->
          <template v-if="getErrors(sales.previewData).length">
            <h4 class="section-title">错误清单（{{ getErrors(sales.previewData).length }} 条）</h4>
            <el-table
              :data="getErrors(sales.previewData)"
              border
              size="small"
              stripe
              height="300"
              :row-class-name="() => 'error-row'"
            >
              <el-table-column type="expand">
                <template slot-scope="scope">
                  <div class="expand-msg">行号：{{ scope.row.rowNo }} | 错误信息：{{ scope.row.msg }}</div>
                </template>
              </el-table-column>
              <el-table-column prop="rowNo" label="行号" width="90"></el-table-column>
              <el-table-column prop="orderNo" label="配镜单号" width="180"></el-table-column>
              <el-table-column prop="msg" label="错误信息" show-overflow-tooltip></el-table-column>
            </el-table>
          </template>

          <!-- commit 结果 -->
          <el-alert
            v-if="sales.commitData"
            :title="`导入完成：成功订单 ${sales.commitData.orderInserted || 0} 条，新建客户 ${sales.commitData.customerCreated || 0} 个，跳过 ${sales.commitData.skipped || 0} 条，错误 ${(sales.commitData.errors || []).length} 条`"
            type="success"
            :closable="false"
            show-icon
            style="margin-top: 16px;"
          ></el-alert>
        </el-card>
      </el-tab-pane>

      <!-- ================= Tab2 验光数据导入 ================= -->
      <el-tab-pane label="验光数据导入" name="optometry">
        <el-card shadow="never">
          <el-upload
            drag
            action="#"
            :auto-upload="false"
            :on-change="(f, l) => onFileChange(f, l, 'optometry')"
            :on-remove="() => onFileRemove('optometry')"
            :limit="1"
            :file-list="optometry.fileList"
            accept=".xls,.xlsx,.html"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击选择</em></div>
            <div slot="tip" class="el-upload__tip">验光+视功能数据导入（模板248列，自动生成验光记录与视功能记录），单个文件不超过 20MB；选择文件后自动解析预览</div>
          </el-upload>

          <el-row :gutter="16" v-if="optometry.previewData">
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ optometry.previewData.totalRows }}</div><div class="stat-label">总行数</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ optometry.previewData.validRows }}</div><div class="stat-label">有效行数</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ optometry.previewData.optometryCount }}</div><div class="stat-label">验光记录数</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ optometry.previewData.visionCount }}</div><div class="stat-label">视功能记录数</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ optometry.previewData.customerToCreate }}</div><div class="stat-label">需新建客户</div></el-card></el-col>
            <el-col :span="4"><el-card shadow="hover" class="stat-card"><div class="stat-num">{{ optometry.previewData.skipped }}</div><div class="stat-label">跳过数</div></el-card></el-col>
          </el-row>

          <div class="action-bar" v-if="optometry.file">
            <el-button
              type="primary"
              icon="el-icon-check"
              :loading="optometry.committing"
              :disabled="!optometry.previewData"
              @click="commitImport('optometry')"
            >确认导入</el-button>
            <el-button
              v-if="optometry.commitData && optometry.commitData.errors && optometry.commitData.errors.length"
              type="warning"
              icon="el-icon-download"
              @click="downloadErrorCsv('验光', optometry.commitData.errors)"
            >下载错误CSV</el-button>
          </div>

          <template v-if="getPreviewRows(optometry.previewData).length">
            <h4 class="section-title">预览明细（最多前 20 条）</h4>
            <el-table :data="getPreviewRows(optometry.previewData).slice(0, 20)" border size="small" stripe>
              <el-table-column
                v-for="col in getPreviewColumns(optometry.previewData)"
                :key="col"
                :prop="col"
                :label="col"
                show-overflow-tooltip
              ></el-table-column>
            </el-table>
          </template>

          <template v-if="getErrors(optometry.previewData).length">
            <h4 class="section-title">错误清单（{{ getErrors(optometry.previewData).length }} 条）</h4>
            <el-table
              :data="getErrors(optometry.previewData)"
              border
              size="small"
              stripe
              height="300"
            >
              <el-table-column prop="rowNo" label="行号" width="90"></el-table-column>
              <el-table-column prop="msg" label="错误信息" show-overflow-tooltip></el-table-column>
            </el-table>
          </template>

          <el-alert
            v-if="optometry.commitData"
            :title="`导入完成：验光记录 ${optometry.commitData.optometryInserted || 0} 条，视功能记录 ${optometry.commitData.visionInserted || 0} 条，新建客户 ${optometry.commitData.customerCreated || 0} 个，新建处方 ${optometry.commitData.prescriptionCreated || 0} 张，跳过 ${optometry.commitData.skipped || 0} 条，错误 ${(optometry.commitData.errors || []).length} 条`"
            type="success"
            :closable="false"
            show-icon
            style="margin-top: 16px;"
          ></el-alert>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
const MAX_SIZE = 20 * 1024 * 1024 // 20MB

export default {
  name: 'Import',
  data () {
    return {
      activeTab: 'sales',
      sales: this.initBucket(),
      optometry: this.initBucket(),
      tplLoading: { sales: false, optometry: false }
    }
  },
  methods: {
    // ===== 模板下载（走 axios 带 token，blob 保存） =====
    downloadTemplate (type, fallbackName) {
      this.tplLoading[type] = true
      this.$axios.http
        .get(`/import/templates/${type}`, { responseType: 'blob' })
        .then(res => {
          const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' })
          let filename = fallbackName
          const cd = res.headers['content-disposition']
          if (cd) {
            const m = /filename\*?=(?:UTF-8'')?([^;]+)/i.exec(cd)
            if (m && m[1]) filename = decodeURIComponent(m[1].trim().replace(/^"|"$/g, ''))
          }
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob)
          a.download = filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(a.href)
        })
        .catch(() => {
          this.$message.error('模板下载失败，请稍后重试')
        })
        .finally(() => {
          this.tplLoading[type] = false
        })
    },
    initBucket () {
      return {
        file: null,
        fileList: [],
        previewing: false,
        previewData: null,
        committing: false,
        commitData: null
      }
    },
    bucket (type) {
      return type === 'sales' ? this.sales : this.optometry
    },
    onFileChange (file, fileList, type) {
      const bucket = this.bucket(type)
      // 大小校验
      if (file.size > MAX_SIZE) {
        this.$message.error('文件大小不能超过 20MB')
        bucket.fileList = []
        bucket.file = null
        this.clearResult(bucket)
        return
      }
      // limit=1，只保留最新选中的文件
      bucket.fileList = fileList.slice(-1)
      bucket.file = bucket.fileList[0].raw
      this.clearResult(bucket)
      this.doPreview(type)
    },
    onFileRemove (type) {
      const bucket = this.bucket(type)
      bucket.file = null
      bucket.fileList = []
      this.clearResult(bucket)
    },
    clearResult (bucket) {
      bucket.previewData = null
      bucket.commitData = null
    },
    buildFormData (bucket) {
      const fd = new FormData()
      fd.append('file', bucket.file)
      return fd
    },
    doPreview (type) {
      const bucket = this.bucket(type)
      bucket.previewing = true
      this.$axios.http
        .post(`/import/${type}/preview`, this.buildFormData(bucket))
        .then(res => {
          if (res.data.status === 200) {
            bucket.previewData = res.data.data || {}
          } else {
            this.$message.error(res.data.message || '预览解析失败')
          }
        })
        .catch(() => {
          this.$message.error('预览请求失败，请检查网络或后端服务')
        })
        .finally(() => {
          bucket.previewing = false
        })
    },
    commitImport (type) {
      const bucket = this.bucket(type)
      bucket.committing = true
      this.$axios.http
        .post(`/import/${type}/commit`, this.buildFormData(bucket))
        .then(res => {
          if (res.data.status === 200) {
            bucket.commitData = res.data.data || {}
            this.$message.success('导入完成')
            // commit 成功后清空文件选择
            bucket.file = null
            bucket.fileList = []
            bucket.previewData = null
          } else {
            this.$message.error(res.data.message || '导入失败')
          }
        })
        .catch(() => {
          this.$message.error('导入请求失败，请检查网络或后端服务')
        })
        .finally(() => {
          bucket.committing = false
        })
    },
    // ===== 动态预览明细：以后端实际返回为准 =====
    getPreviewRows (data) {
      if (!data) return []
      // 优先取 preview 字段；若无则尝试常见明细字段
      const rows = data.preview || data.records || data.list || data.details
      return Array.isArray(rows) ? rows : []
    },
    getPreviewColumns (data) {
      const rows = this.getPreviewRows(data)
      if (!rows.length) return []
      // 以第一行为基准提取列名
      const keys = Object.keys(rows[0])
      // 过滤内部无意义字段
      return keys.filter(k => typeof rows[0][k] !== 'object')
    },
    getErrors (data) {
      if (!data || !data.errors) return []
      return data.errors
    },
    // ===== 客户端导出错误 CSV（UTF-8 BOM，Excel 不乱码） =====
    downloadErrorCsv (label, errors) {
      const header = ['行号', '单号', '错误信息']
      const escape = v => {
        if (v === null || v === undefined) return ''
        const s = String(v).replace(/"/g, '""')
        return `"${s}"`
      }
      const lines = [header.join(',')]
      errors.forEach(e => {
        lines.push([escape(e.rowNo), escape(e.orderNo || e.cardNo || ''), escape(e.msg)].join(','))
      })
      const csv = '\uFEFF' + lines.join('\r\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      const ts = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${label}导入错误-${ts}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(a.href)
    }
  }
}
</script>

<style lang="less" scoped>
.import-page {
  padding: 10px;
}
.action-bar {
  margin: 16px 0;
}
.section-title {
  margin: 20px 0 10px;
  color: #333;
  font-size: 15px;
}
.stat-card {
  text-align: center;
  .stat-num {
    font-size: 26px;
    font-weight: bold;
    color: #409EFF;
  }
  .stat-label {
    margin-top: 6px;
    color: #909399;
    font-size: 13px;
  }
}
.expand-msg {
  padding: 10px 20px;
  color: #606266;
}
::v-deep .error-row {
  background-color: #fef0f0;
}
</style>
